import { computed, onBeforeMount, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import { crossbeamApi } from '@/api';
import useAwsPartnerCentral from '@/composables/useAwsPartnerCentral';
import useFeedDetails from '@/composables/useFeedDetails';
import {
  AWS_PARTNER_CENTRAL_DATA_SOURCE_TYPE,
  DATABRICKS_DATA_SOURCE_TYPE,
  DELL_SPIL_DATA_SOURCE_TYPE,
  EXTERNAL_FILE_DATA_SOURCE_TYPE,
  FILE_UPLOAD_DATA_SOURCE_TYPE,
  GOOGLE_SHEETS_DATA_SOURCE_TYPE,
  SALESFORCE_DATA_SOURCE_TYPE,
  SNOWFLAKE_DATA_SOURCE_TYPE,
  SOURCE_STATUS_MAP,
  patchFeed,
} from '@/constants/data_sources';
import {
  BIG_CSV_FILE_LIMIT_500MB,
  TEMP_ALLOW_ADDING_TO_BIG_CSV,
} from '@/constants/feature_flags';
import { captureException } from '@/errors';
import {
  useDataSharesStore,
  useFeatureFlagStore,
  useFeedsStore,
  useFileUploadsStore,
  useFlashesStore,
  usePotentialRevenueStore,
  useSourcesStore,
} from '@/stores';
import { Feed, FeedFrequency } from '@/types/feeds';
import { PotentialRevenueSettings } from '@/types/potential_revenue';

import useAuth from './useAuth';

/* Used to provide state and handlers for the data source settings modal,
 * including patching sync frequency, loading + saving state, etc. */

const loading = ref(true);
const feed = ref<Feed | null>(null);
const isSyncing = ref(false);
const currentFrequency = ref<FeedFrequency>(undefined);

const fieldMappingSettings = ref<{
  settingsCmp?: { saveSettings?: () => Promise<void> };
  potentialRevenueSettings?: PotentialRevenueSettings;
  hasBusinessSettingsChanged?: boolean;
  hasProductTypeChanged?: boolean;
}>({});

// Callback to reset initial states after save
const fieldMappingSaveCallback = ref<(() => void) | null>(null);

export default function () {
  const route = useRoute();
  const router = useRouter();
  const flashesStore = useFlashesStore();
  const dataSharesStore = useDataSharesStore();
  const feedsStore = useFeedsStore();
  const fileUploadsStore = useFileUploadsStore();
  const sourcesStore = useSourcesStore();
  // AWS Partner Central lives on an offline-partner org, so its feed/source are
  // not in the shared stores; resolve them from here as a fallback.
  const {
    refreshAwsPartnerCentral,
    awsPartnerCentralOrgUuid,
    getAwsFeedById,
    getAwsSourceById,
    getAwsSourcesByFeedId,
  } = useAwsPartnerCentral();
  const potRevSettingsStore = usePotentialRevenueStore();
  const { hasFeatureFlag } = useFeatureFlagStore();
  const { preferredCRMFeedId } = useAuth();

  const feedId = computed(() => Number(route.params.id));
  const sourceId = computed(() => Number(route.params.source_id));
  const saving = ref(false);

  const isCSVFeed = computed(
    () => feed.value?.integration?.type === FILE_UPLOAD_DATA_SOURCE_TYPE,
  );
  const isGSheetFeed = computed(
    () => feed.value?.integration?.type === GOOGLE_SHEETS_DATA_SOURCE_TYPE,
  );
  const isSnowflakeFeed = computed(
    () => feed.value?.integration?.type === SNOWFLAKE_DATA_SOURCE_TYPE,
  );
  const isDatabricksFeed = computed(
    () => feed.value?.integration?.type === DATABRICKS_DATA_SOURCE_TYPE,
  );
  const isDellSpilFeed = computed(
    () => feed.value?.integration?.type === DELL_SPIL_DATA_SOURCE_TYPE,
  );
  const isExternalFileDataFeed = computed(
    () => feed.value?.integration?.type === EXTERNAL_FILE_DATA_SOURCE_TYPE,
  );
  // `aws_partner_central` is not yet in the generated OpenAPI feed-type union,
  // so cast to string to compare.
  const isAwsPartnerCentralFeed = computed(
    () =>
      (feed.value?.integration?.type as string) ===
      AWS_PARTNER_CENTRAL_DATA_SOURCE_TYPE,
  );
  const isPreferredCRMFeed = computed(
    () => feedId.value === preferredCRMFeedId.value,
  );

  const source = computed(() => {
    if (sourceId.value) {
      return (
        sourcesStore.getSourceById(sourceId.value) ||
        getAwsSourceById(sourceId.value) ||
        null
      );
    }
    return (
      sourcesStore
        .getSourcesByFeedId(feedId.value)
        .find((source) => source.mdm_type === 'account') ||
      getAwsSourcesByFeedId(feedId.value).find(
        (source) => source.mdm_type === 'account',
      ) ||
      null
    );
  });

  const sourceDisabled = computed(() => {
    if (!source.value?.status) return false;
    return (
      (SOURCE_STATUS_MAP.process as readonly string[]).includes(
        source.value.status,
      ) ||
      (SOURCE_STATUS_MAP.deleting as readonly string[]).includes(
        source.value.status,
      )
    );
  });

  const sourceDeleting = computed(() => {
    if (!source.value?.status) return false;
    return (SOURCE_STATUS_MAP.deleting as readonly string[]).includes(
      source.value.status,
    );
  });

  const hasBigCSVFileLimit = computed(() =>
    hasFeatureFlag(BIG_CSV_FILE_LIMIT_500MB),
  );
  const allowAddingToBigFiles = computed(() =>
    hasFeatureFlag(TEMP_ALLOW_ADDING_TO_BIG_CSV),
  );

  const allowAddData = computed(
    () =>
      isCSVFeed.value &&
      (!hasBigCSVFileLimit.value || allowAddingToBigFiles.value),
  );

  const uploadedByEmail = computed(() => {
    const currentUpload = fileUploadsStore.currentUploads.find(
      (upload) => upload.base_source_id === source.value?.id,
    );
    return currentUpload?.created_by_user_email;
  });

  /* On initial component mount, populate the initial values for feed, frequency.
  If the feedId value from the route changes (feedId changes) then
  reset all of these values for the new feed */

  async function setValues() {
    if (feedId.value && feed.value?.id !== feedId.value) {
      loading.value = true;
      await feedsStore.readySync;
      feed.value = feedsStore.getFeedById(feedId.value) || null;
      if (!feed.value) {
        // Not an own-org feed — could be the AWS Partner Central offline-partner
        // feed, which isn't in FeedsStore. Fetch it and resolve from there.
        // Catch so a failed refresh (which now throws) doesn't leave the
        // settings page stuck loading; the feed just resolves as not-found.
        await refreshAwsPartnerCentral().catch(captureException);
        feed.value = getAwsFeedById(feedId.value) || null;
      }
      isSyncing.value = !feed.value?.is_paused;
      currentFrequency.value = feed.value?.frequency as FeedFrequency;
    }

    loading.value = false;
  }

  onBeforeMount(setValues);
  watch([feedId], setValues);

  /* Update feed settings */
  async function handlePatchFeed() {
    saving.value = true;
    try {
      if (!feed.value) throw new Error('Feed not found');
      const offlinePartnerOrgUuid = isAwsPartnerCentralFeed.value
        ? ((await awsPartnerCentralOrgUuid()) ?? undefined)
        : undefined;
      // Without the offline-partner uuid the PATCH would target the customer
      // org, so abort rather than save against the wrong org (matches delete).
      if (isAwsPartnerCentralFeed.value && !offlinePartnerOrgUuid) {
        throw new Error(
          'Missing AWS Partner Central org uuid; aborting settings save',
        );
      }
      await patchFeed(
        feed.value.id,
        {
          is_paused: !isSyncing.value,
          frequency: currentFrequency.value ?? undefined,
        },
        offlinePartnerOrgUuid,
      );
      flashesStore.addSuccessFlash({ message: 'Settings saved' });
      await Promise.all([
        dataSharesStore.refreshDataSharesStore(),
        feedsStore.refreshFeedsStore(),
        sourcesStore.refreshSourcesStore(),
        // AWS Partner Central feeds live outside FeedsStore; refresh them too so
        // the saved pause/frequency reflects in the data-sources list. Catch so a
        // failed refetch doesn't report a save error after the save succeeded.
        ...(isAwsPartnerCentralFeed.value
          ? [refreshAwsPartnerCentral().catch(captureException)]
          : []),
      ]);
    } catch (err) {
      captureException(err);
      flashesStore.addErrorFlash({
        message: 'Could not save settings',
        description: 'If the error persists contact support@crossbeam.com',
      });
    } finally {
      saving.value = false;
    }
  }

  /* Save field mapping settings (amount, owner, product fields) */
  async function handleSaveFieldMappingSettings() {
    saving.value = true;
    try {
      const { potentialRevenueSettings, settingsCmp } =
        fieldMappingSettings.value;

      if (potentialRevenueSettings) {
        const {
          amount_column_potential_influence: amountColumnPotentialInfluence,
          opportunity_owner_id_column: opportunityOwnerIdColumn,
        } = potentialRevenueSettings;

        const body: Pick<
          PotentialRevenueSettings,
          'amount_column_potential_influence' | 'opportunity_owner_id_column'
        > = {};

        if (amountColumnPotentialInfluence !== undefined) {
          body.amount_column_potential_influence =
            amountColumnPotentialInfluence;
        }

        if (opportunityOwnerIdColumn !== undefined) {
          body.opportunity_owner_id_column = opportunityOwnerIdColumn;
        }

        await crossbeamApi.POST(
          '/v0.1/organization/settings/potential-influence',
          { body },
        );
      }

      const { hasBusinessSettingsChanged, hasProductTypeChanged } =
        fieldMappingSettings.value;

      if (hasBusinessSettingsChanged && settingsCmp?.saveSettings) {
        await settingsCmp.saveSettings();
      }

      if (hasProductTypeChanged) {
        await potRevSettingsStore.saveProductTypeSettings();
      }

      if (potentialRevenueSettings || hasProductTypeChanged) {
        await potRevSettingsStore.refreshPotentialRevenueStore();
      }

      flashesStore.addSuccessFlash({
        message: 'Potential revenue settings saved',
        description:
          'It will take some time for these changes to be reflected.',
      });

      // Trigger callback after successful save so FieldsSection can reset initial states
      fieldMappingSaveCallback.value?.();
    } catch (err) {
      captureException(err);
      flashesStore.addErrorFlash({
        message: 'Could not save field mapping settings',
        description: `If this error persists, please contact
          <a target="_blank" href="mailto:support@crossbeam.com">support@crossbeam.com</a>.`,
      });
    } finally {
      saving.value = false;
    }
  }

  /* Reauthorize feed */
  const { reauthorize } = useFeedDetails({ feedId });
  async function handleReauthorize() {
    if (!feed.value) return;

    /* For Salesforce with JWT authorization, open the JWT modal */
    if (
      feed.value.integration?.type === SALESFORCE_DATA_SOURCE_TYPE &&
      hasFeatureFlag('salesforce_jwt_authorization')
    ) {
      await router.push({
        name: 'jwt_reauthorize',
        params: { type: feed.value.integration.type },
        query: { feed_id: feed.value.id },
      });
      return;
    }

    try {
      await reauthorize(feed.value);
    } catch (err) {
      captureException(err);
      flashesStore.addErrorFlash({
        message: 'Could not reauthorize feed',
        description: 'If the error persists contact support@crossbeam.com',
      });
    }
  }

  return {
    handlePatchFeed,
    handleSaveFieldMappingSettings,
    handleReauthorize,
    loading,
    saving,
    isSyncing,
    feed,
    source,
    isCSVFeed,
    isGSheetFeed,
    isSnowflakeFeed,
    isDellSpilFeed,
    isExternalFileDataFeed,
    isAwsPartnerCentralFeed,
    isPreferredCRMFeed,
    isDatabricksFeed,
    currentFrequency,
    allowAddData,
    sourceDisabled,
    sourceDeleting,
    uploadedByEmail,
    // Field mapping
    fieldMappingSettings,
    fieldMappingSaveCallback,
  };
}
